<template>
  <div class="plan-step">
    <div class="billing-toggle-row">
      <v-btn-toggle
        :model-value="billingPeriod"
        mandatory
        divided
        density="comfortable"
        class="billing-toggle-group"
        @update:model-value="$emit('update:billingPeriod', $event)"
      >
        <v-btn value="annual" class="billing-toggle-btn">Annual</v-btn>
        <v-btn value="monthly" class="billing-toggle-btn">Monthly</v-btn>
      </v-btn-toggle>
    </div>
    <p class="billing-hint">With annual billing you get two months free.</p>

    <!-- Either self-serve plan can be picked whatever the location count from
         the first step; the count only sets how many locations it is billed
         for. Established is priced on request and handled on the first step. -->
    <div class="plan-options" role="radiogroup" aria-label="Choose your plan">
      <div
        v-for="plan in planOptions"
        :key="plan.value"
        class="plan-option"
        :class="{ 'plan-option--selected': plan.value === packagePlan }"
        role="radio"
        :aria-checked="plan.value === packagePlan ? 'true' : 'false'"
        tabindex="0"
        @click="choosePlan(plan.value)"
        @keydown.enter.prevent="choosePlan(plan.value)"
        @keydown.space.prevent="choosePlan(plan.value)"
      >
        <div class="plan-option-header">
          <span class="plan-option-radio" aria-hidden="true"></span>
          <div class="plan-option-heading">
            <div class="plan-select-name">{{ plan.name }}</div>
            <div class="plan-select-price">
              <span class="plan-select-amount">£{{ displayMonthly(plan) }}</span>
              <span class="plan-select-period">/mo{{ plan.perLocation ? " per location" : "" }}</span>
            </div>
            <div class="plan-select-billing-note">{{ billingNoteFor(plan) }}</div>
            <div class="plan-select-locations-note">{{ selfServeLocationsNote }}</div>
          </div>
        </div>
        <div class="plan-select-divider"></div>
        <div v-if="plan.inherits" class="plan-select-inherits">
          Everything in {{ plan.inherits }}, plus:
        </div>
        <ul class="plan-select-features">
          <li v-for="feature in plan.features" :key="feature" class="plan-select-feature">
            <v-icon class="plan-feature-icon">mdi-check</v-icon>
            {{ feature }}
          </li>
        </ul>
      </div>

    </div>
    <p v-if="planMissingError" class="step-validation-error" role="alert">
      {{ planMissingError }}
    </p>

    <!-- The summary shows what is actually charged. The annual price already
         has the two free months in it, so the breakdown multiplies out to the
         total with no separate discount line. -->
    <div v-if="selectedPlan" class="order-summary">
      <div class="order-summary-row">
        <span>{{ selectedPlan.name }} plan{{ billingPeriod === "annual" ? " (annual)" : "" }}</span>
        <span v-if="billingPeriod === 'annual'">£{{ selectedPlanAnnualTotal }}/year</span>
        <span v-else>£{{ selectedPlanMonthlyTotal }}/mo</span>
      </div>
      <div v-if="selectedPlan.perLocation" class="order-summary-breakdown">
        {{ billedLocations }} {{ billedLocations === 1 ? "location" : "locations" }} × £{{ selectedPlanUnitPrice }} per location
      </div>
      <div class="order-summary-row order-summary-discount">
        <span>30-day free trial</span>
        <span>£0</span>
      </div>
      <div class="order-summary-divider"></div>
      <div class="order-summary-row order-summary-total">
        <span>Due today</span>
        <span>£0</span>
      </div>
      <div class="order-summary-trial-note">
        <template v-if="billingPeriod === 'annual'">
          Then £{{ selectedPlanAnnualTotal }}/year (£{{ selectedPlanAnnualPerMonth }}/mo equivalent) after your 30-day free trial
        </template>
        <template v-else>
          Then £{{ selectedPlanMonthlyTotal }}/mo after your 30-day free trial
        </template>
      </div>
    </div>

    <div class="stripe-secure-badge">
      <v-icon class="stripe-icon">mdi-lock</v-icon>
      <span>Payments securely processed by <strong>Stripe</strong></span>
    </div>
    <p class="stripe-next-note">
      Next: add a payment card on Stripe. You won't be charged until your free trial ends.
    </p>

    <div class="terms-agreement-row">
      <input
        id="register-terms-checkbox"
        type="checkbox"
        class="terms-checkbox"
        :checked="agreed"
        @change="$emit('update:agreed', $event.target.checked)"
      />
      <label for="register-terms-checkbox" class="terms-label">
        I agree to the
        <button type="button" class="inline-link" @click="$emit('show-terms')">
          Business terms &amp; conditions
        </button>.
      </label>
    </div>
    <p v-if="termsMissingError" class="step-validation-error" role="alert">
      {{ termsMissingError }}
    </p>

    <div class="ios-warning">
      <input
        id="register-ios-checkbox"
        type="checkbox"
        class="ios-warning-checkbox"
        :checked="iosAcknowledged"
        @change="$emit('update:iosAcknowledged', $event.target.checked)"
      />
      <label for="register-ios-checkbox" class="ios-warning-label">
        <v-icon class="ios-warning-icon">mdi-information-outline</v-icon>
        <span>
          I understand that, at the moment, business scanning is supported on iOS only.
        </span>
      </label>
    </div>
    <p v-if="iosAckMissingError" class="step-validation-error" role="alert">
      {{ iosAckMissingError }}
    </p>
  </div>
</template>

<script>
import { computed } from "vue";
import {
  PLAN_PRICING,
  SELF_SERVE_LOCATIONS_NOTE,
  formatGbp,
  monthlyEquivalent,
} from "@/utils/pricingPlans.js";

// The self-serve plans, in display order. Prices and the per-location flag
// come from the shared pricing module; features stay here. `inherits` names
// the plan a tier builds on so the shared features aren't repeated.
const PLAN_OPTIONS = [
  {
    value: "starter",
    name: "Starter",
    ...PLAN_PRICING.starter,
    features: [
      "Unlimited scans",
      "Branded iOS & Android digital rewards cards",
      "Redemption-ready push alerts",
    ],
  },
  {
    value: "scaler",
    name: "Scaler",
    ...PLAN_PRICING.scaler,
    inherits: "Starter",
    features: [
      "Loyalty analytics",
      "Remote stamping with automatic return-visit push alerts",
      "Custom starting stamps for campaigns",
    ],
  },
];

// Final step of business sign-up: pick a plan, choose a billing period, see
// the order summary and tick the two consent checkboxes (business terms and
// the iOS-only scanning limitation). The parent owns the values and the
// terms dialog; this component emits changes.
export default {
  name: "PlanStep",
  emits: [
    "update:packagePlan",
    "update:billingPeriod",
    "update:agreed",
    "update:iosAcknowledged",
    "show-terms",
  ],
  props: {
    // From the first step. Only matters for per-location plans, where it is
    // how many times the plan is billed. Arrives as a string from the number
    // field, so it's parsed here.
    numberOfLocations: {
      type: [Number, String],
      default: 1,
    },
    // "starter" | "scaler" | "" (nothing picked yet)
    packagePlan: {
      type: String,
      default: "",
    },
    // "annual" | "monthly"
    billingPeriod: {
      type: String,
      default: "annual",
    },
    agreed: {
      type: Boolean,
      default: false,
    },
    // Separate from the terms so the iOS-only limitation is read and ticked
    // on its own.
    iosAcknowledged: {
      type: Boolean,
      default: false,
    },
    // True once the owner has tried to continue, so the plan and consent
    // errors only appear after an attempt rather than on first render.
    validationShown: {
      type: Boolean,
      default: false,
    },
  },
  setup(props, { emit }) {
    const choosePlan = (value) => {
      if (value !== props.packagePlan) emit("update:packagePlan", value);
    };

    // The headline per-month figure follows the toggle: the monthly price,
    // or what a year works out at per month with the two free months in.
    const displayMonthly = (plan) =>
      monthlyEquivalent(plan.value, props.billingPeriod);

    const billingNoteFor = (plan) => {
      if (props.billingPeriod === "annual") {
        return plan.perLocation
          ? `£${plan.annual} per location, billed annually`
          : `£${plan.annual} billed annually`;
      }
      return plan.perLocation
        ? `£${plan.monthly} per location, billed monthly`
        : `£${plan.monthly} billed monthly`;
    };

    const selectedPlan = computed(
      () => PLAN_OPTIONS.find((p) => p.value === props.packagePlan) || null
    );

    // Per-location plans are billed once for every location.
    const billedLocations = computed(() => {
      if (!selectedPlan.value?.perLocation) return 1;
      const count = parseInt(props.numberOfLocations, 10);
      return Number.isInteger(count) && count >= 1 ? count : 1;
    });

    // What one location costs for the chosen billing period.
    const selectedPlanUnitPrice = computed(() => {
      const plan = selectedPlan.value;
      if (!plan) return "";
      return props.billingPeriod === "annual"
        ? `${plan.annual}/year`
        : `${plan.monthly}/mo`;
    });

    const selectedPlanMonthlyTotal = computed(() =>
      selectedPlan.value ? selectedPlan.value.monthly * billedLocations.value : 0
    );

    const selectedPlanAnnualTotal = computed(() =>
      selectedPlan.value ? selectedPlan.value.annual * billedLocations.value : 0
    );

    // The annual charge spread over twelve months, matching the per-month
    // figure on the plan cards.
    const selectedPlanAnnualPerMonth = computed(() =>
      formatGbp(selectedPlanAnnualTotal.value / 12)
    );

    const planMissingError = computed(() =>
      props.validationShown && !props.packagePlan
        ? "Please choose a plan to continue."
        : ""
    );

    const termsMissingError = computed(() =>
      props.validationShown && !props.agreed
        ? "Please accept the terms to continue."
        : ""
    );

    const iosAckMissingError = computed(() =>
      props.validationShown && !props.iosAcknowledged
        ? "Please confirm you understand the iOS-only limitation."
        : ""
    );

    return {
      planOptions: PLAN_OPTIONS,
      selfServeLocationsNote: SELF_SERVE_LOCATIONS_NOTE,
      choosePlan,
      selectedPlan,
      billedLocations,
      selectedPlanUnitPrice,
      selectedPlanMonthlyTotal,
      selectedPlanAnnualTotal,
      selectedPlanAnnualPerMonth,
      planMissingError,
      termsMissingError,
      iosAckMissingError,
      displayMonthly,
      billingNoteFor,
    };
  },
};
</script>

<style scoped>
.plan-step {
  display: flex;
  flex-direction: column;
}

/* Billing toggle */
.billing-toggle-row {
  display: flex;
  justify-content: center;
  margin-bottom: 8px;
}

.billing-hint {
  text-align: center;
  font-size: 12px;
  color: #4a5568;
  margin: 0 0 16px;
}

.billing-toggle-group {
  border-radius: 999px !important;
  overflow: hidden;
  border: 1px solid rgba(0, 109, 62, 0.3);
  background: #ffffff;
}

.billing-toggle-btn {
  text-transform: none !important;
  font-weight: 600 !important;
  font-size: 13px !important;
  letter-spacing: 0 !important;
  padding: 0 18px !important;
  color: var(--lulu-primary) !important;
  background: #ffffff !important;
}

.billing-toggle-btn.v-btn--active {
  background: var(--lulu-primary) !important;
  color: #ffffff !important;
}

/* Plan cards */
.plan-options {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 10px;
  margin: 12px 0 10px;
}

.plan-option {
  display: flex;
  flex-direction: column;
  background: #ffffff;
  border: 2px solid var(--lulu-border);
  border-radius: 14px;
  padding: 16px 14px 14px;
  text-align: left;
  cursor: pointer;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.plan-option:hover,
.plan-option:focus-visible {
  border-color: var(--lulu-primary);
  outline: none;
}

.plan-option--selected {
  border-color: var(--lulu-primary);
  box-shadow: 0 0 0 3px rgba(0, 109, 62, 0.1);
}

.plan-option-header {
  display: flex;
  align-items: flex-start;
  gap: 10px;
}

.plan-option-radio {
  position: relative;
  flex-shrink: 0;
  width: 18px;
  height: 18px;
  margin-top: 1px;
  border: 2px solid var(--lulu-border);
  border-radius: 50%;
  background: #ffffff;
}

.plan-option--selected .plan-option-radio {
  border-color: var(--lulu-primary);
}

.plan-option--selected .plan-option-radio::after {
  content: "";
  position: absolute;
  inset: 3px;
  border-radius: 50%;
  background: var(--lulu-primary);
}

.plan-option-heading {
  min-width: 0;
}

.plan-select-name {
  font-size: 15px;
  font-weight: 700;
  color: var(--lulu-text-strong);
  margin-bottom: 6px;
}

.plan-select-price {
  display: flex;
  align-items: baseline;
  gap: 1px;
  margin-bottom: 4px;
}

.plan-select-amount {
  font-size: 26px;
  font-weight: 800;
  color: var(--lulu-primary);
}

.plan-select-period {
  font-size: 12px;
  color: var(--lulu-text-muted);
  font-weight: 500;
}

.plan-select-billing-note {
  font-size: 12px;
  color: #4a5568;
  margin-bottom: 2px;
}

.plan-select-locations-note {
  font-size: 11px;
  font-weight: 700;
  color: #4a5568;
  margin-bottom: 10px;
}

.plan-select-divider {
  border-top: 1px solid var(--lulu-border);
  margin-bottom: 10px;
}

.plan-select-inherits {
  font-size: 12px;
  font-weight: 700;
  color: var(--lulu-primary);
  margin-bottom: 6px;
}

.plan-select-features {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.plan-select-feature {
  display: flex;
  align-items: flex-start;
  font-size: 12px;
  color: #4a5568;
  line-height: 1.4;
}

.plan-feature-icon {
  font-size: 14px !important;
  color: var(--lulu-primary);
  margin-right: 6px;
  flex-shrink: 0;
}

.inline-link {
  background: none;
  border: none;
  padding: 0;
  font-size: 13px;
  font-weight: 600;
  color: var(--lulu-primary);
  cursor: pointer;
  text-decoration: underline;
  font-family: inherit;
}

.inline-link:hover {
  color: var(--lulu-primary-dark);
}

/* Order summary */
.order-summary {
  background: var(--lulu-surface-soft);
  border: 1px solid var(--lulu-border);
  border-radius: 12px;
  padding: 16px;
  margin-top: 14px;
}

.order-summary-row {
  display: flex;
  justify-content: space-between;
  font-size: 13px;
  color: #4a5568;
  margin-bottom: 8px;
}

.order-summary-breakdown {
  font-size: 12px;
  color: var(--lulu-text-muted);
  margin: -4px 0 8px;
}

.order-summary-discount {
  color: var(--lulu-primary);
  font-weight: 500;
}

.order-summary-divider {
  border-top: 1px solid var(--lulu-border);
  margin: 10px 0;
}

.order-summary-total {
  font-size: 15px;
  font-weight: 700;
  color: var(--lulu-text-strong);
  margin-bottom: 0;
}

.order-summary-trial-note {
  font-size: 12px;
  color: #4a5568;
  text-align: center;
  margin-top: 8px;
}

.stripe-secure-badge {
  display: flex;
  align-items: center;
  font-size: 12px;
  color: var(--lulu-text-muted);
  margin-top: 16px;
}

.stripe-icon {
  font-size: 15px !important;
  color: #635bff;
  margin-right: 6px;
}

.stripe-next-note {
  font-size: 12px;
  color: #4a5568;
  line-height: 1.5;
  margin: 6px 0 0;
}

/* Consent */
.terms-agreement-row {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin-top: 14px;
  padding: 12px 14px;
  background: var(--lulu-surface-soft);
  border: 1px solid var(--lulu-border);
  border-radius: 10px;
}

.terms-checkbox {
  width: 16px;
  height: 16px;
  flex-shrink: 0;
  margin-top: 2px;
  accent-color: var(--lulu-primary);
  cursor: pointer;
}

.terms-label {
  font-size: 13px;
  color: #4a5568;
  line-height: 1.5;
  cursor: pointer;
}

/* iOS-only acknowledgement, styled as a warning so it stands apart from
   the terms consent. */
.ios-warning {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin-top: 14px;
  padding: 12px 14px;
  background: #fffbeb;
  border: 1px solid #fcd34d;
  border-radius: 10px;
  font-size: 13px;
  color: #92400e;
  font-weight: 500;
}

.ios-warning-checkbox {
  width: 16px;
  height: 16px;
  flex-shrink: 0;
  margin-top: 2px;
  accent-color: #b45309;
  cursor: pointer;
}

.ios-warning-label {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  cursor: pointer;
  line-height: 1.5;
}

.ios-warning-icon {
  font-size: 16px !important;
  color: #b45309;
  flex-shrink: 0;
  margin-top: 1px;
}

.step-validation-error {
  margin: 6px 0 0;
  color: var(--lulu-danger-strong);
  font-size: 12px;
  font-weight: 500;
}
</style>
